import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { StatusBadge } from "@/components/Badges";
import { ArrowLeft, Plus, Printer, Trash2, Save, ChevronDown, StickyNote, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import Logo from "@/components/Logo";
import { translateChecklistLabel } from "@/lib/checklistLabels";
import { useTimeFormat } from "@/hooks/useTimeFormat";
import WorkItemRow, { computeTotals, computeTotalsVat, autoCalcPrice, computeTotalWorkTimeAw } from "@/components/WorkItemRow";
import { usePricing } from "@/hooks/usePricing";
import NotesPanel from "@/components/NotesPanel";
import SendEmailDialog from "@/components/SendEmailDialog";

const STATUSES = ["new","planned","in_progress","waiting_parts","waiting_customer","finished","billed","cancelled"];
const PRIORITIES = ["low","normal","high","urgent"];

export default function ContractDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const confirm = useConfirm();
  const [c, setC] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [audit, setAudit] = useState([]);
  const [settings, setSettings] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const tf = useTimeFormat();
  const isMechanic = user?.role === "mechanic";

  const load = async () => {
    const [cr, cs, vs, es, ts, au, st] = await Promise.all([
      api.get(`/contracts/${id}`), api.get("/customers"), api.get("/vehicles"),
      api.get("/employees"), api.get("/checklist-templates"), api.get(`/contracts/${id}/audit`),
      api.get("/settings"),
    ]);
    setC(cr.data); setCustomers(cs.data); setVehicles(vs.data); setEmployees(es.data);
    setTemplates(ts.data); setAudit(au.data); setSettings(st.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const customer = customers.find((x) => x.id === c?.customer_id);
  const vehicle = vehicles.find((x) => x.id === c?.vehicle_id);
  const assignedEmp = employees.find((x) => x.id === c?.assigned_employee_id);
  const totalAw = useMemo(() => computeTotalWorkTimeAw(c?.work_items || []), [c]);
  const hourlyRate = Number(settings?.hourly_rate ?? 100);
  const pricing = usePricing();
  const totals = useMemo(
    () => computeTotals(c?.work_items || [], hourlyRate),
    [c, hourlyRate]
  );
  const vatTotals = useMemo(
    () => computeTotalsVat(c?.work_items || [], hourlyRate, pricing.defaultVat),
    [c, hourlyRate, pricing.defaultVat]
  );
  const totalPrice = totals.grand;
  const deposit = parseFloat(c?.deposit_amount || 0);
  const remaining = c?.payment_status === "paid" ? 0 : Math.max(0, totalPrice - (c?.payment_status === "deposit" ? deposit : 0));

  const handlePrint = (mode) => {
    // mode: "both" | "contract" | "checklist"
    document.body.classList.remove("print-mode-both", "print-mode-contract", "print-mode-checklist");
    document.body.classList.add(`print-mode-${mode}`);
    // Use requestAnimationFrame so CSS applies before print dialog opens
    requestAnimationFrame(() => {
      setTimeout(() => {
        window.print();
        setTimeout(() => {
          document.body.classList.remove(`print-mode-${mode}`);
        }, 500);
      }, 50);
    });
  };

  const patch = async (changes) => {
    const { data } = await api.patch(`/contracts/${id}`, changes);
    setC(data);
    setAudit((await api.get(`/contracts/${id}/audit`)).data);
  };

  const save = async () => {
    try {
      await patch({
        title: c.title, description: c.description, status: c.status,
        priority: c.priority, assigned_employee_id: c.assigned_employee_id || null,
        planned_date: c.planned_date || null, planned_time: c.planned_time || null,
        payment_status: c.payment_status, deposit_amount: parseFloat(c.deposit_amount || 0),
        work_items: c.work_items, checklist: c.checklist,
      });
      toast.success(t("common.save"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const addWorkItem = () => {
    setC({ ...c, work_items: [...(c.work_items || []), {
      id: crypto.randomUUID(), type: "labor", description: "",
      hours: 1, hourly_rate: hourlyRate, price: hourlyRate,
      aw: 0, completed: false, notes: "", price_manual: false,
    }] });
  };

  const toggleWorkItem = async (idx, completed) => {
    const items = [...c.work_items];
    items[idx] = { ...items[idx], completed };
    const allDone = items.length > 0 && items.every((w) => w.completed);
    if (allDone && c.status !== "finished") {
      if (await confirm({
        title: t("contracts.completion_prompt"),
        description: "",
        confirmLabel: t("statuses.finished"),
        danger: false,
      })) {
        await patch({ work_items: items, status: "finished" });
        toast.success("Finished");
        return;
      }
    }
    await patch({ work_items: items });
  };

  const setChecklist = async (idx, changes) => {
    const arr = [...c.checklist];
    arr[idx] = { ...arr[idx], ...changes };
    await patch({ checklist: arr });
  };

  const addChecklistItem = () => {
    setC({ ...c, checklist: [...(c.checklist || []), { id: crypto.randomUUID(), label: "", result: "unchecked", notes: "" }] });
  };

  const applyTemplate = async (tplId) => {
    const tpl = templates.find((x) => x.id === tplId);
    if (!tpl) return;
    const items = tpl.items.map((label) => ({ id: crypto.randomUUID(), label, result: "unchecked", notes: "" }));
    await patch({ checklist: [...(c.checklist || []), ...items] });
    toast.success(tpl.name);
  };

  if (!c) return <div className="text-zinc-400">{t("common.loading")}</div>;

  return (
    <div className="space-y-6" data-testid="contract-detail-page">
      {/* Print-only header (logo + workshop info) */}
      <div className="print-header">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
          <Logo size={64} />
          <div style={{ textAlign: "right", fontSize: 11, color: "#444", lineHeight: 1.4 }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{settings?.workshop_name || "Autordo Werkstatt"}</div>
            {settings?.address && <div>{settings.address}</div>}
            {settings?.phone && <div>Tel: {settings.phone}</div>}
            {settings?.email && <div>{settings.email}</div>}
          </div>
        </div>
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #999", display: "flex", justifyContent: "space-between", fontSize: 12 }}>
          <div><strong>{t("contracts.number")}:</strong> {c.number}</div>
          <div><strong>{t("contracts.plan_date")}:</strong> {c.planned_date || "—"} {c.planned_time || ""}</div>
          <div><strong>{t("contracts.assigned_to")}:</strong> {assignedEmp ? `${assignedEmp.first_name} ${assignedEmp.last_name}` : "—"}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 no-print">
        <Button variant="ghost" onClick={() => nav(-1)} className="text-zinc-300 hover:bg-[#2A2F3A]">
          <ArrowLeft className="h-4 w-4 mr-1" /> {t("common.back")}
        </Button>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="text-zinc-300 hover:bg-[#2A2F3A]" data-testid="print-btn">
                <Printer className="h-4 w-4 mr-1" /> {t("common.print")} <ChevronDown className="h-3 w-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#21252E] border-[#2E3241] text-white">
              <DropdownMenuItem onClick={() => handlePrint("both")} data-testid="print-both">
                {t("contracts.details")} + {t("contracts.checklist")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handlePrint("contract")} data-testid="print-contract">
                {t("contracts.details")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handlePrint("checklist")} data-testid="print-checklist">
                {t("contracts.checklist")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/contracts/${id}/pdf?mode=contract`, "_blank")}
                data-testid="download-pdf-contract"
              >
                {t("emails.download_pdf")} · {t("emails.pdf_mode_contract")}
              </DropdownMenuItem>
              {!isMechanic && (
                <DropdownMenuItem
                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/contracts/${id}/pdf?mode=invoice`, "_blank")}
                  data-testid="download-pdf-invoice"
                >
                  {t("emails.download_pdf")} · {t("emails.pdf_mode_invoice")}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {!isMechanic && customer?.email && (
            <Button
              variant="outline"
              onClick={() => setEmailOpen(true)}
              className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10"
              data-testid="contract-email-btn"
            >
              <Mail className="h-4 w-4 mr-1" /> {t("emails.send")}
            </Button>
          )}
          {!isMechanic && (
            <Button onClick={save} className="bg-blue-500 hover:bg-blue-600" data-testid="contract-save-btn">
              <Save className="h-4 w-4 mr-1" /> {t("common.save")}
            </Button>
          )}
          {!isMechanic && (
            <Button
              variant="ghost"
              onClick={async () => {
                if (!(await confirm({
                  title: t("confirm.title"),
                  description: t("confirm.description"),
                  confirmLabel: t("common.delete"),
                }))) return;
                try {
                  await api.delete(`/contracts/${id}`);
                  toast.success(t("common.delete"));
                  nav("/contracts");
                } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
              }}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              data-testid="contract-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" /> {t("common.delete")}
            </Button>
          )}
        </div>
      </div>

      <div className="surface rounded-xl p-6" data-print-section="contract">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t("contracts.number")}</div>
            <h1 className="font-display text-3xl font-semibold mt-1">{c.number}</h1>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <StatusBadge status={c.status} />
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t("contracts.working_time")}</div>
              <div className="font-display text-2xl font-mono-stat" data-testid="contract-working-time">{tf.format(totalAw)}</div>
            </div>
            {pricing.enabled && (
              <div className="text-right no-print">
                <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t("contracts.total_price")}</div>
                <div className="font-display text-2xl font-mono-stat text-emerald-300" data-testid="total-price">{vatTotals.gross.toFixed(2)} €</div>
                <div className="text-[11px] text-zinc-500 font-mono-stat" data-testid="total-net-vat">
                  {t("pricing.net")} {vatTotals.net.toFixed(2)} · {t("pricing.vat")} {vatTotals.vat.toFixed(2)}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-6">
          <div>
            <Label className="text-xs">{t("common.description")}</Label>
            <Input value={c.title || ""} onChange={(e) => setC({ ...c, title: e.target.value })}
              disabled={isMechanic} className="bg-[#171B24] border-[#2E3241] mt-1" data-testid="contract-title" />
          </div>
          <div>
            <Label className="text-xs">{t("common.status")}</Label>
            <Select value={c.status} onValueChange={(v) => setC({ ...c, status: v })}>
              <SelectTrigger className="bg-[#171B24] border-[#2E3241] mt-1" data-testid="contract-status-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#21252E] border-[#2E3241] text-white">
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`statuses.${s}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">{t("common.priority")}</Label>
            <Select value={c.priority} onValueChange={(v) => setC({ ...c, priority: v })}>
              <SelectTrigger className="bg-[#171B24] border-[#2E3241] mt-1" disabled={isMechanic}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#21252E] border-[#2E3241] text-white">
                {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{t(`priorities.${p}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">{t("contracts.assigned_to")}</Label>
            <Select value={c.assigned_employee_id || ""} onValueChange={(v) => setC({ ...c, assigned_employee_id: v })}>
              <SelectTrigger className="bg-[#171B24] border-[#2E3241] mt-1" disabled={isMechanic}><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent className="bg-[#21252E] border-[#2E3241] text-white">
                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">{t("contracts.plan_date")}</Label>
            <Input type="date" value={c.planned_date || ""} onChange={(e) => setC({ ...c, planned_date: e.target.value })}
              disabled={isMechanic} className="bg-[#171B24] border-[#2E3241] mt-1" data-testid="contract-date" />
          </div>
          <div>
            <Label className="text-xs">{t("contracts.plan_time")}</Label>
            <Input type="time" value={c.planned_time || ""} onChange={(e) => setC({ ...c, planned_time: e.target.value })}
              disabled={isMechanic} className="bg-[#171B24] border-[#2E3241] mt-1" />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mt-6">
          <div className="surface rounded-lg p-4">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t("contracts.customer")}</div>
            <div className="mt-2 space-y-1 text-sm">
              <div className="font-display font-semibold text-base">{customer?.name || "—"}</div>
              <div className="text-zinc-400">{customer?.phone}</div>
              <div className="text-zinc-400">{customer?.email}</div>
              <div className="text-zinc-400">{customer?.address}</div>
            </div>
          </div>
          <div className="surface rounded-lg p-4">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t("contracts.vehicle")}</div>
            <div className="mt-2 space-y-1 text-sm">
              <div className="font-display font-semibold text-base">{vehicle ? `${vehicle.brand} ${vehicle.model}` : "—"}</div>
              <div className="text-zinc-400">{t("vehicles.plate")}: {vehicle?.license_plate}</div>
              <div className="text-zinc-400">VIN: {vehicle?.vin || "—"}</div>
              <div className="text-zinc-400">{t("vehicles.mileage")}: {vehicle?.mileage} km</div>
            </div>
          </div>
        </div>

        <Textarea value={c.description || ""} onChange={(e) => setC({ ...c, description: e.target.value })} disabled={isMechanic}
          placeholder={t("common.notes")} rows={3} className="mt-4 bg-[#171B24] border-[#2E3241]" />
      </div>

      {pricing.enabled && (
        <div className="surface rounded-xl p-6 no-print" data-print-section="payment" data-testid="payment-section">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-3">{t("contracts.payment")}</div>
          <div className="grid md:grid-cols-4 gap-4 items-end">
            <div>
              <Label className="text-xs">{t("common.status")}</Label>
              <Select value={c.payment_status || "unpaid"} onValueChange={(v) => setC({ ...c, payment_status: v })}>
                <SelectTrigger className="bg-[#171B24] border-[#2E3241] mt-1" data-testid="payment-status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#21252E] border-[#2E3241] text-white">
                  <SelectItem value="unpaid">{t("contracts.payment_status.unpaid")}</SelectItem>
                  <SelectItem value="deposit">{t("contracts.payment_status.deposit")}</SelectItem>
                  <SelectItem value="paid">{t("contracts.payment_status.paid")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {c.payment_status === "deposit" && (
              <div>
                <Label className="text-xs">{t("contracts.deposit_amount")} (€)</Label>
                <Input type="number" step="0.01" value={c.deposit_amount || 0}
                  onChange={(e) => setC({ ...c, deposit_amount: e.target.value })}
                  className="bg-[#171B24] border-[#2E3241] mt-1 font-mono-stat" data-testid="deposit-amount-input" />
              </div>
            )}
            <div>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t("contracts.total_price")}</div>
              <div className="font-display text-xl font-mono-stat mt-1">{totalPrice.toFixed(2)} €</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t("contracts.remaining")}</div>
              <div className={`font-display text-xl font-mono-stat mt-1 ${remaining === 0 ? "text-emerald-300" : "text-amber-300"}`} data-testid="remaining-amount">
                {remaining.toFixed(2)} €
              </div>
            </div>
          </div>
        </div>
      )}

      <Tabs defaultValue="work" className="space-y-3">
        <TabsList className="bg-[#21252E] border border-[#2E3241] no-print">
          <TabsTrigger value="work" data-testid="tab-work">{t("contracts.work_items")}</TabsTrigger>
          <TabsTrigger value="check" data-testid="tab-checklist">{t("contracts.checklist")}</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">
            <StickyNote className="h-3.5 w-3.5 mr-1" />{t("notes.title")}
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">{t("contracts.audit")}</TabsTrigger>
        </TabsList>

        <TabsContent value="work" className="surface rounded-xl p-4 space-y-2" data-print-section="work">
          {(c.work_items || []).length === 0 && <p className="text-sm text-zinc-500">{t("common.empty")}</p>}
          {(c.work_items || []).map((w, idx) => (
            <WorkItemRow
              key={w.id || idx}
              item={w}
              idx={idx}
              disabled={isMechanic}
              defaultRate={hourlyRate}
              pricingEnabled={pricing.enabled}
              vatEnabled={pricing.enabled}
              defaultVat={pricing.defaultVat}
              vatOptions={pricing.vatOptions}
              onChange={(next) => setC({
                ...c,
                work_items: c.work_items.map((x, i) => i === idx ? {
                  ...next,
                  price: autoCalcPrice(next, hourlyRate),
                } : x),
              })}
              onToggle={(v) => toggleWorkItem(idx, v)}
              onDelete={() => setC({ ...c, work_items: c.work_items.filter((_, i) => i !== idx) })}
            />
          ))}
          {!isMechanic && (
            <div className="flex items-center justify-between pt-1">
              <Button variant="ghost" onClick={addWorkItem} className="text-blue-400 hover:bg-[#2A2F3A]" data-testid="add-work-item-btn">
                <Plus className="h-4 w-4 mr-1" /> {t("contracts.add_work_item")}
              </Button>
              {(c.work_items || []).length > 0 && (
                <TotalsSummary totals={totals} vatTotals={pricing.enabled ? vatTotals : null} />
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="check" className="surface rounded-xl p-4 space-y-2" data-print-section="checklist">
          {!isMechanic && (
            <div className="flex items-center gap-2 mb-2 no-print">
              <Select onValueChange={applyTemplate}>
                <SelectTrigger className="bg-[#171B24] border-[#2E3241] w-72" data-testid="apply-template-select">
                  <SelectValue placeholder={t("contracts.apply_template")} />
                </SelectTrigger>
                <SelectContent className="bg-[#21252E] border-[#2E3241] text-white">
                  {templates.map((tpl) => <SelectItem key={tpl.id} value={tpl.id}>{translateChecklistLabel(tpl.name, t)}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="ghost" onClick={addChecklistItem} className="text-blue-400">
                <Plus className="h-4 w-4 mr-1" /> {t("common.new")}
              </Button>
            </div>
          )}
          {(c.checklist || []).length === 0 && <p className="text-sm text-zinc-500">{t("common.empty")}</p>}
          {(c.checklist || []).map((it, idx) => (
            <div key={it.id} className="grid grid-cols-12 gap-2 items-center">
              <Input
                value={translateChecklistLabel(it.label, t)}
                onChange={(e) =>
                  setC({ ...c, checklist: c.checklist.map((x, i) => i === idx ? { ...x, label: e.target.value } : x) })}
                disabled={isMechanic}
                data-testid={`checklist-label-${idx}`}
                className="col-span-6 bg-[#171B24] border-[#2E3241]" />
              <div className="col-span-3 flex gap-1">
                {["unchecked", "io", "nio"].map((r) => (
                  <Button
                    key={r}
                    variant={it.result === r ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setChecklist(idx, { result: r })}
                    data-testid={`check-${idx}-${r}`}
                    className={`text-xs ${
                      it.result === r
                        ? r === "io" ? "bg-emerald-500 hover:bg-emerald-600 text-white"
                        : r === "nio" ? "bg-red-500 hover:bg-red-600 text-white"
                        : "bg-zinc-700 hover:bg-zinc-600 text-white"
                        : "text-zinc-400 hover:bg-[#2A2F3A]"
                    }`}
                  >
                    {t(`checklist.${r}`)}
                  </Button>
                ))}
              </div>
              <Input value={it.notes || ""} onChange={(e) =>
                setC({ ...c, checklist: c.checklist.map((x, i) => i === idx ? { ...x, notes: e.target.value } : x) })}
                placeholder={t("common.notes")} className="col-span-2 bg-[#171B24] border-[#2E3241]" />
              {!isMechanic && (
                <Button variant="ghost" size="icon" onClick={() =>
                  setC({ ...c, checklist: c.checklist.filter((_, i) => i !== idx) })}
                  className="text-zinc-400 hover:text-red-400">
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="audit" className="surface rounded-xl p-4" data-print-section="audit">
          {audit.length === 0 ? <p className="text-sm text-zinc-500">{t("common.empty")}</p> : (
            <ul className="space-y-2 text-sm">
              {audit.map((a) => (
                <li key={a.id} className="flex items-start gap-3 border-b border-[#2E3241] py-2">
                  <div className="text-xs font-mono-stat text-zinc-500 w-44">{new Date(a.timestamp).toLocaleString()}</div>
                  <div className="text-zinc-300">{a.user_name}</div>
                  <div className="text-zinc-400 flex-1">{a.action} — {a.detail}</div>
                </li>
              ))}
            </ul>
          )}
        </TabsContent>

        <TabsContent value="notes" className="surface rounded-xl p-4" data-print-section="notes">
          <NotesPanel entityType="contract" entityId={id} />
        </TabsContent>
      </Tabs>

      {!isMechanic && customer?.email && (
        <SendEmailDialog
          open={emailOpen}
          onOpenChange={setEmailOpen}
          customerId={customer.id}
          customerName={customer.name}
          customerEmail={customer.email}
          vehicleId={vehicle?.id}
          contractId={id}
        />
      )}

      {/* Print-only signature block */}
      <div className="print-header" data-print-section="signatures" style={{ marginTop: 24, display: "none" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40, marginTop: 32, paddingTop: 16 }}>
          <div>
            <div style={{ borderTop: "1px solid #333", paddingTop: 6, fontSize: 11, color: "#444" }}>
              {t("contracts.customer")}
            </div>
          </div>
          <div>
            <div style={{ borderTop: "1px solid #333", paddingTop: 6, fontSize: 11, color: "#444" }}>
              {t("contracts.assigned_to")}
            </div>
          </div>
        </div>
        <div style={{ fontSize: 10, color: "#777", marginTop: 16, textAlign: "center" }}>
          Autordo · {settings?.workshop_name || ""} · {new Date().toLocaleDateString()}
        </div>
      </div>
    </div>
  );
}

function TotalsSummary({ totals, vatTotals }) {
  const { t } = useTranslation();
  const Row = ({ label, value, accent, testid }) => (
    <div className="flex items-center gap-3" data-testid={testid}>
      <span className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</span>
      <span className={`font-mono-stat text-sm ${accent || "text-zinc-200"}`}>{value.toFixed(2)} €</span>
    </div>
  );
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs" data-testid="work-totals">
      {totals.labor > 0 && <Row label={t("workitem.subtotal_labor")} value={totals.labor} />}
      {totals.parts > 0 && <Row label={t("workitem.subtotal_parts")} value={totals.parts} />}
      {totals.flat  > 0 && <Row label={t("workitem.subtotal_flat")}  value={totals.flat} />}
      {totals.other > 0 && <Row label={t("workitem.subtotal_other")} value={totals.other} />}
      {vatTotals && (
        <>
          <Row label={t("pricing.net")}   value={vatTotals.net}   testid="totals-net" />
          <Row label={t("pricing.vat")}   value={vatTotals.vat}   testid="totals-vat" />
          <Row label={t("pricing.gross")} value={vatTotals.gross} accent="text-emerald-300 font-semibold" testid="totals-gross" />
        </>
      )}
      {!vatTotals && (
        <Row label={t("workitem.grand_total")} value={totals.grand} accent="text-emerald-300 font-semibold" />
      )}
    </div>
  );
}
